import { useEffect, useState } from 'react';
import axios from 'axios';
import { ITopic } from 'app/shared/model/topic.model';

// Ids of the top level topics whose sub topics belong to each module (Admin > Topics).
// The ids differ per environment, so they are looked up by where the app runs; the topics can be renamed freely.
const MODULE_PARENT_TOPIC_IDS = {
  local: { housing: 70, governance: 71 },
  dev: { housing: 62, governance: 63 },
  prod: { housing: 299, governance: 301 },
};
const PROD_HOSTNAMES = ['dashboard.5plus.us'];
const LOCAL_HOSTNAMES = ['localhost', '127.0.0.1'];

// every other host uses the dev ids
const { hostname } = window.location;
const environment = LOCAL_HOSTNAMES.includes(hostname) ? 'local' : PROD_HOSTNAMES.includes(hostname) ? 'prod' : 'dev';

export const MODULE_PARENT_TOPIC_ID = MODULE_PARENT_TOPIC_IDS[environment];

// A module parent topic is still found by its id after a rename, but deleting it would empty the module's topic dropdown
export const isLockedTopic = (topic?: ITopic | null): boolean => !!topic && Object.values(MODULE_PARENT_TOPIC_ID).includes(topic.id);

// Why a locked topic cannot be deleted, e.g. for a tooltip; null when the topic is not locked
export const lockedTopicReason = (topic?: ITopic | null): string | null => {
  if (!topic) return null;
  if (topic.id === MODULE_PARENT_TOPIC_ID.housing) return 'Cannot be deleted: its sub topics are used by the Housing module.';
  if (topic.id === MODULE_PARENT_TOPIC_ID.governance) return 'Cannot be deleted: its sub topics are used by the Good Governance module.';
  return null;
};

export interface ITopicOption {
  value: number;
  label: string;
}

/** Sub topics of one top level topic, e.g. only the Housing sub topics inside the Housing module. */
export const useModuleTopics = (parentId: number): { options: ITopicOption[]; loading: boolean } => {
  const [options, setOptions] = useState<ITopicOption[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      setLoading(true);
      try {
        const children = await axios.get<ITopic[]>(`api/topics?parentId.equals=${parentId}&page=0&size=100&sort=name,asc`);
        if (!cancelled) setOptions((children.data || []).map(topic => ({ value: topic.id, label: topic.name })));
      } catch (e) {
        if (!cancelled) setOptions([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => {
      cancelled = true;
    };
  }, [parentId]);

  return { options, loading };
};
